import _ from "lodash";

/**
 * Count of DQNs grouped by Reason — column bar chart.
 */
export const countByReason = {
    title: { text: "DQNs by Reason" },
    dataFilter({ items }) {
        return _.chain(items)
            .filter(i => !i.Cancelled)
            .groupBy(i => i.Reason || "Unknown")
            .toPairs()
            .map(([reason, dqns]) => ({ reason, count: dqns.length }))
            .orderBy("count", "desc")
            .value();
    },
    chart: {
        title: { text: "DQNs by Reason" },
        series: [{
            type: "bar",
            xKey: "reason",
            yKey: "count",
            yName: "Count",
        }],
        axes: [{
            type: "category",
            position: "left",
            title: { text: "Reason" },
        }, {
            type: "number",
            position: "bottom",
            title: { text: "Count" },
        }],
    },
    table: {
        columnDefs: [{
            headerName: "Reason",
            field: "reason",
            flex: 2,
        }, {
            headerName: "Count",
            field: "count",
            type: "number",
            sort: "desc",
            flex: 1,
        }],
    },
};

/**
 * Resolved vs Unresolved split — donut (pie) chart.
 */
export const resolvedStatus = {
    title: { text: "Resolved vs Unresolved" },
    dataFilter({ items }) {
        const active = items.filter(i => !i.Cancelled);
        const resolved = active.filter(i => i.Signedoff).length;
        const unresolved = active.filter(i => !i.Signedoff).length;
        return [
            { status: "Resolved", count: resolved },
            { status: "Unresolved", count: unresolved },
        ];
    },
    chart: {
        title: { text: "Resolved vs Unresolved" },
        series: [{
            type: "pie",
            angleKey: "count",
            calloutLabelKey: "status",
            sectorLabelKey: "count",
            innerRadiusRatio: 0.6,
            fills: ["#22c55e", "#ef4444"],
            strokes: ["#16a34a", "#dc2626"],
        }],
    },
};

/**
 * DQNs created per month — column trend chart with year selector.
 */
export const trendOverTime = {
    title: { text: "DQNs Created Over Time" },
    yearSelector: true,
    dataFilter({ items, state }) {
        return _.chain(items)
            .filter(i => !i.Cancelled)
            .filter(i => {
                const year = new Date(i?.Date?.Created).getFullYear();
                return year === state.year;
            })
            .groupBy(i => {
                const d = new Date(i?.Date?.Created);
                return isNaN(d) ? "Unknown" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            })
            .toPairs()
            .map(([month, dqns]) => ({ month, count: dqns.length }))
            .orderBy("month", "asc")
            .value();
    },
    chart: {
        title: { text: "DQNs Created Over Time" },
        series: [{
            type: "column",
            xKey: "month",
            yKey: "count",
            yName: "DQNs Created",
        }],
        axes: [{
            type: "category",
            position: "bottom",
            title: { text: "Month" },
            label: { rotation: -45 },
        }, {
            type: "number",
            position: "left",
            title: { text: "Count" },
        }],
    },
};

/**
 * Reason breakdown by resolution status — stacked column chart.
 */
export const reasonByResolution = {
    title: { text: "Reason by Resolution Status" },
    dataFilter({ items }) {
        return _.chain(items)
            .filter(i => !i.Cancelled)
            .groupBy(i => i.Reason || "Unknown")
            .toPairs()
            .map(([reason, dqns]) => ({
                reason,
                resolved: dqns.filter(i => i.Signedoff).length,
                unresolved: dqns.filter(i => !i.Signedoff).length,
            }))
            .orderBy(d => d.resolved + d.unresolved, "desc")
            .value();
    },
    chart: {
        title: { text: "Reason by Resolution Status" },
        series: [{
            type: "column",
            xKey: "reason",
            yKey: "resolved",
            yName: "Resolved",
            stacked: true,
            fill: "#22c55e",
            stroke: "#16a34a",
        }, {
            type: "column",
            xKey: "reason",
            yKey: "unresolved",
            yName: "Unresolved",
            stacked: true,
            fill: "#ef4444",
            stroke: "#dc2626",
        }],
        axes: [{
            type: "category",
            position: "bottom",
            title: { text: "Reason" },
            label: { rotation: -30 },
        }, {
            type: "number",
            position: "left",
            title: { text: "Count" },
        }],
    },
    table: {
        columnDefs: [{
            headerName: "Reason",
            field: "reason",
            flex: 2,
        }, {
            headerName: "Resolved",
            field: "resolved",
            type: "number",
            flex: 1,
        }, {
            headerName: "Unresolved",
            field: "unresolved",
            type: "number",
            flex: 1,
        }],
    },
};
